// Idle-deferred SW registration, update-prompt state, and silent version
// re-hydration for the 8 bootstrap collection stores.
//
// Deferral is modeled on 06.posthog.client.ts: requestIdleCallback with a 1s
// setTimeout fallback keeps registration off the critical path to first
// paint/hydration. Unlike posthog-js, `virtual:pwa-register`'s registerSW()
// (node_modules/vite-plugin-pwa/dist/client/build/register.js) has no eager
// side effects at import time — it only calls `import('workbox-window')`
// *inside* the function body, which we don't invoke until the idle callback
// fires — so a static top-level import here is safe and doesn't need the
// dynamic-import trick posthog-js required.
//
// Registration goes through `virtual:pwa-register` rather than the module's
// `$pwa` injection: nuxt.config.ts sets `pwa.client.registerPlugin: false`
// specifically because @vite-pwa/nuxt's own client plugin calls
// `useRegisterSW({ immediate: true })` unconditionally as soon as it mounts
// (confirmed by grepping a production build for `vite-pwa:nuxt:client:plugin`
// before that config line was added) — eager, and it would double-register
// the SW alongside this plugin. With registerPlugin disabled, `$pwa` is never
// injected, so `virtual:pwa-register` is the only avenue left.
//
// It does NOT type-resolve out of the box: `.nuxt/nuxt.d.ts` only pulls in
// vite-plugin-pwa's `/vue`, `/info`, and `/pwa-assets` ambient modules, none
// of which declare the bare `virtual:pwa-register` specifier (confirmed via
// `npx tsc --noEmit -p .nuxt/tsconfig.json` -> TS2307 without the reference
// below). `vite-plugin-pwa/vanillajs.d.ts` is the one that declares it.
/// <reference types="vite-plugin-pwa/vanillajs" />
import { registerSW } from 'virtual:pwa-register';

import { useSitesStore } from '@/stores/sites';
import { useNeighborhoodsStore } from '@/stores/neighborhoods';
import { usePointsOfInterestStore } from '@/stores/points-of-interest';
import { useToursStore } from '@/stores/tours';
import { useTrailsStore } from '@/stores/trails';
import { useEventsStore } from '@/stores/events';
import { useSponsorsStore } from '@/stores/sponsors';
import { usePartnersStore } from '@/stores/partners';
import { hydrateCollectionStore } from '@/utils/defineCollectionStore';
import { staleCollectionKeys } from '@/utils/bootstrapPayload';

// How often a long-lived tab asks for a new service worker. registerSW()
// only checks at registration; the browser's own check runs only on
// navigations, which an SPA session (or a home-screen app resumed from the
// background) may not make for days. Hourly, plus on returning to the tab.
const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000;
const SW_UPDATE_ON_VISIBLE_MIN_GAP_MS = 10 * 60 * 1000;

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();

    // Kill switch: NUXT_PUBLIC_PWA_DISABLED=true ships a self-destroying SW
    // (nuxt.config.ts `pwa.selfDestroying`) that unregisters itself and clears
    // every cache on its own. This plugin does nothing in that mode — no idle
    // timer, no registration, no message listener — so it can't interfere.
    if (config.public.pwaDisabled) return;

    // Same 'bootstrap-version' useState key 02.store-initialization.ts sets
    // during SSR/hydration. Reading it here (instead of a fresh ref) means the
    // BOOTSTRAP_VERSION comparison below is always against whatever version
    // the stores actually hold, however they got there (SSR payload, client
    // fetch, or a previous re-hydration).
    const bootstrapVersion = useState<string | null>('bootstrap-version', () => null);

    // Toast-facing state read by app/components/Global/pwa-update-toast.vue.
    const needRefresh = useState('pwa-need-refresh', () => false);
    const updateServiceWorkerFn = useState<((reloadPage?: boolean) => Promise<void>) | null>('pwa-update-fn', () => null);

    // Bootstrap collection key -> store instance, matching /api/bootstrap's
    // BOOTSTRAP_KEYS and 02.store-initialization.ts's storesByKey (points-of-interest's
    // KV key differs from its Pinia store id, hence the explicit map rather
    // than deriving it from the store ids).
    const storesByKey: Record<string, any> = {
        sites: useSitesStore(),
        neighborhoods: useNeighborhoodsStore(),
        events: useEventsStore(),
        tours: useToursStore(),
        trails: useTrailsStore(),
        'points-of-interest': usePointsOfInterestStore(),
        sponsors: useSponsorsStore(),
        partners: usePartnersStore(),
    };

    const init = () => {
        updateServiceWorkerFn.value = registerSW({
            // We've already deferred the *call* to registerSW via
            // requestIdleCallback/setTimeout above; `immediate: true` here just
            // tells workbox-window to register right away instead of also
            // waiting on its own for the window 'load' event on top of that.
            immediate: true,
            onNeedRefresh() {
                needRefresh.value = true;
            },
            onOfflineReady() {
                // No UI for this yet — offline readiness is silent by design
                // (only the update prompt is user-facing per the brief).
            },
            onRegisteredSW(_swUrl: string, registration: ServiceWorkerRegistration | undefined) {
                if (!registration) return;
                let lastCheck = Date.now();
                const check = async () => {
                    // Offline or mid-install: update() would just reject.
                    if (registration.installing || navigator.onLine === false) return;
                    lastCheck = Date.now();
                    try {
                        await registration.update();
                    } catch {
                        // Network blip; the next interval or visit retries.
                    }
                };
                setInterval(check, SW_UPDATE_INTERVAL_MS);
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'visible' && Date.now() - lastCheck > SW_UPDATE_ON_VISIBLE_MIN_GAP_MS) check();
                });
            },
        });

        if (!('serviceWorker' in navigator)) return;

        // Ask the worker to pre-fetch the photos the app shows first, so they
        // still appear offline without having been scrolled to (app/sw.ts,
        // warmOfflineImages). Cheap when they're already cached.
        navigator.serviceWorker.ready.then((registration) => registration.active?.postMessage({ type: 'WARM_IMAGES' })).catch(() => {});

        navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
            if (event.data?.type !== 'BOOTSTRAP_VERSION') return;

            // Per collection, not just the aggregate version: a page rendered
            // at the current version can still have collections the server
            // left out of its payload (app/utils/bootstrapPayload.ts), and an
            // aggregate-equal broadcast must still fill those in. Collections
            // already at the incoming version are left alone.
            for (const key of staleCollectionKeys(storesByKey, event.data.collections)) {
                hydrateCollectionStore(storesByKey[key], event.data.collections[key]);
            }
            bootstrapVersion.value = event.data.version ?? null;
        });
    };

    if ('requestIdleCallback' in window) requestIdleCallback(init, { timeout: 3000 });
    else setTimeout(init, 1000);
});
